import Vue from 'vue'
import Router from 'vue-router'
import { requiresAdmin, requiresSession } from './guards'
import store from './store'

Vue.use(Router)

export default new Router({
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import(/* webpackChunkName: "home" */ './views/Home.vue')
    },
    {
      path: '/about',
      name: 'about',
      component: () => import(/* webpackChunkName: "about" */ './views/About.vue')
    },
    {
      path: '/signin',
      name: 'signin',
      component: () => import(/* webpackChunkName: "signin" */ './views/SignIn.vue'),
      // Somebody who is already signed in has no business on this form: it
      // would sign them in as somebody else without their signing out first,
      // and it would leave them on a screen whose only purpose is not being
      // signed in. Per-route rather than global, because the routes that need
      // guarding -- the ones that read private data -- have nothing to protect
      // until they exist. They exist now, and they carry the other guard.
      beforeEnter: (to, from, next) => {
        next(store.getters.signedIn ? { name: 'home' } : undefined)
      }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import(/* webpackChunkName: "profile" */ './views/Profile.vue'),
      // The screen for the signed-in user's own account, and so a route that reads
      // private data like `/lists` above: `requiresSession` rather than an inline
      // guard of its own, because the rule is the same rule and a second copy of
      // it is a second copy to drift.
      //
      // The read behind it is `GET /api/users/profile`, which the server answers
      // with the caller's own record and nobody else's -- so an unguarded route
      // would not leak anything, it would only answer `401` and bounce through the
      // interceptor to the sign-in screen anyway. The guard makes that the direct
      // answer rather than a request that fails first, and it also remembers the
      // route, so signing in from here lands back here rather than on the home
      // page. See `guards.ts`.
      beforeEnter: requiresSession
    },
    {
      path: '/list/:id',
      name: 'list-detail',
      component: () => import(/* webpackChunkName: "list-detail" */ './views/ListDetail.vue')
    },
    {
      // The signed-in user's own lists, which is the first route here that reads
      // private data, and so the first that needs a session. `GET /api/lists`
      // answers with the caller's active lists and nothing else, so there is
      // nothing in this route that browsing does not already show -- the point of
      // it is the private half, and this ticket's reasons for showing names only
      // are in `views/MyLists.vue`.
      //
      // Guarded per-route rather than by a global `beforeEach`, because the
      // public routes above must stay reachable without a session: browsing is
      // public and the public namespace is not made authentication-aware.
      path: '/lists',
      name: 'lists',
      component: () => import(/* webpackChunkName: "lists" */ './views/MyLists.vue'),
      beforeEnter: requiresSession
    },
    {
      // The administrative route, guarded on the admin flag as well as the
      // session. Ticket 13 fills in the screen; what this ticket needs of it is
      // the guard, and the guard is what decides who ever sees it.
      //
      // The server answers `403` on every call under `/api/users` regardless of
      // what the store says, so this is a courtesy. See `guards.ts`.
      path: '/admin/users',
      name: 'admin-users',
      component: () => import(/* webpackChunkName: "admin-users" */ './views/AdminUsers.vue'),
      beforeEnter: requiresAdmin
    }
  ]
})
